import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Plus, Trash2, MessageSquare, Wrench, ChevronRight, Download } from "lucide-react";

// Minimal markdown → HTML (headings, bold, bullets, numbered)
const renderMd = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^#{3,4} (.*)$/gm, "<div class='text-xs uppercase tracking-wider text-cyan-300 font-semibold mt-3 mb-1'>$1</div>")
    .replace(/^#{1,2} (.*)$/gm, "<div class='font-display text-base text-white font-semibold mt-3 mb-1'>$1</div>")
    .replace(/^[-*] (.*)$/gm, "<div class='pl-4 relative before:content-[\"•\"] before:absolute before:left-0 before:text-cyan-300/60'>$1</div>")
    .replace(/^\d+\. (.*)$/gm, "<div class='pl-4 relative text-slate-200'>$1</div>")
    .replace(/\n{2,}/g, "<div class='h-2'></div>")
    .replace(/\n/g, "<br/>");
  return <div className="text-sm text-slate-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
};

const TOOL_LABELS = {
  get_race_snapshot: "Race snapshot",
  get_pending_approvals: "Pending approvals",
  get_calendar: "Calendar",
  get_fundraising_summary: "Fundraising totals",
  get_campaigns: "Campaigns",
  get_tasks: "Tasks",
  get_notifications: "Notifications",
  get_qr_scans: "QR scans",
  create_task: "Task created",
  send_daily_brief_now: "Daily brief sent",
  mark_notifications_read: "Notifications cleared",
};

export default function AskTotalReach() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const r = await api.get("/ask-totalreach/sessions");
      setSessions(r.data.sessions || []);
    } catch { /* ignore */ }
  };
  const loadSession = async (id) => {
    try {
      const r = await api.get(`/ask-totalreach/sessions/${id}`);
      setSid(id);
      setMessages(r.data.messages || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const startNew = () => { setSid(null); setMessages([]); setQ(""); };

  useEffect(() => {
    loadSessions();
    api.get("/ask-totalreach/starter-prompts")
      .then(r => setPrompts(r.data.prompts || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const question = (text ?? q).trim();
    if (!question || sending) return;
    setSending(true);
    setQ("");
    // Optimistic user message
    const optimistic = { role: "user", content: question, ts: new Date().toISOString(), optimistic: true };
    setMessages(m => [...m, optimistic]);
    try {
      const r = await api.post("/ask-totalreach/ask", { question, session_id: sid });
      const nextSid = r.data.session_id;
      const asst = { role: "assistant", content: r.data.reply, ts: new Date().toISOString(),
                     tool_calls: r.data.tool_calls || [] };
      setSid(nextSid);
      setMessages(m => [...m.filter(x => !x.optimistic),
                        { ...optimistic, optimistic: false },
                        asst]);
      loadSessions();
    } catch (e) {
      setMessages(m => m.filter(x => !x.optimistic));
      toast.error(errMsg(e, "Assistant could not respond"));
    } finally {
      setSending(false);
    }
  };

  const removeSession = async (id, ev) => {
    ev.stopPropagation();
    try {
      await api.delete(`/ask-totalreach/sessions/${id}`);
      if (id === sid) startNew();
      loadSessions();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadPdf = async () => {
    if (!sid) { toast.error("Send at least one message before exporting"); return; }
    try {
      const r = await api.get(`/ask-totalreach/sessions/${sid}/pdf`, { responseType: "blob" });
      const dispo = r.headers?.["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || `ask-totalreach-${sid.slice(0, 8)}.pdf`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Briefing PDF downloaded");
    } catch (e) { toast.error(errMsg(e, "Could not export briefing")); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ask-totalreach-page">
      <PageHeader
        eyebrow="AI Chief of Staff"
        title="Ask TotalReach"
        subtitle="Your operations chief. Ask about your race, campaigns, fundraising, approvals and calendar — grounded on live data."
        action={
          <div className="flex gap-2">
            {sid && messages.length > 0 && (
              <Btn onClick={downloadPdf} variant="secondary" data-testid="ask-download-pdf-btn">
                <Download className="w-4 h-4" /> Export briefing
              </Btn>
            )}
            <Btn onClick={startNew} variant="secondary" data-testid="ask-new-session-btn">
              <Plus className="w-4 h-4" /> New chat
            </Btn>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Sessions sidebar */}
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Recent chats</div>
          <div className="space-y-1.5" data-testid="ask-session-list">
            {sessions.length === 0 && (
              <div className="text-xs text-slate-500 px-2 py-4">No chats yet — ask your first question below.</div>
            )}
            {sessions.map(s => (
              <button
                key={s.id}
                onClick={() => loadSession(s.id)}
                data-testid={`ask-session-${s.id}`}
                className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors group flex items-start gap-2 ${sid === s.id ? "bg-cyan-400/10 border border-cyan-400/30 text-white" : "text-slate-300 hover:bg-white/5 border border-transparent"}`}
              >
                <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-300/70" />
                <span className="truncate flex-1">{s.title || "Untitled chat"}</span>
                <Trash2
                  className="w-3.5 h-3.5 text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition"
                  onClick={(ev) => removeSession(s.id, ev)}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Chat pane */}
        <Card padded={false} className="flex flex-col h-[calc(100vh-260px)] min-h-[500px]" data-testid="ask-chat-card">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8" data-testid="ask-empty-state">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-[10px] uppercase tracking-[0.25em] mb-4">
                  <Sparkles className="w-3 h-3" /> Powered by Claude
                </div>
                <div className="font-display text-2xl font-bold text-white mb-2">How can I help you run this campaign?</div>
                <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">
                  I have live access to your race, campaigns, fundraising, approvals and calendar. Try one of these:
                </p>
                <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                  {prompts.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      data-testid={`ask-starter-${i}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user"
                  ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-medium"
                  : "bg-slate-900/60 border border-white/10 text-slate-100"}`}>
                  {m.role === "user"
                    ? <div className="text-sm">{m.content}</div>
                    : renderMd(m.content)}
                  {m.role === "assistant" && (m.tool_calls || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-1.5" data-testid={`ask-tools-${i}`}>
                      {m.tool_calls.map((tc, k) => (
                        <span
                          key={k}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tc.ok !== false ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-300" : "bg-rose-400/10 border border-rose-400/30 text-rose-300"}`}
                        >
                          <Wrench className="w-2.5 h-2.5" /> {TOOL_LABELS[tc.name] || tc.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start" data-testid="ask-typing">
                <div className="bg-slate-900/60 border border-white/10 rounded-2xl px-4 py-3 inline-flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> Ask TotalReach is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/10 p-4 bg-slate-950/60">
            <div className="flex items-end gap-2">
              <textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask about pending approvals, race trends, calendar, top donors…"
                data-testid="ask-input"
                rows={1}
                className="flex-1 bg-slate-900/80 border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500 resize-none min-h-[42px] max-h-[140px]"
              />
              <Btn onClick={() => send()} disabled={sending || !q.trim()} data-testid="ask-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </Btn>
            </div>
            <div className="mt-2 text-[10px] text-slate-600 flex items-center gap-1">
              <ChevronRight className="w-3 h-3" /> Answers are grounded on your active political entity&apos;s live data.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
